<template>
	<div class="stepFeatures">
		<header class="stepFeatures__header">
			<h2 class="stepFeatures__title">{{ $t('setupWizard_features_title') }}</h2>
			<p class="stepFeatures__caption">{{ $t('setupWizard_features_caption') }}</p>
		</header>

		<div class="stepFeatures__list">
			<div class="stepFeatures__row">
				<div class="stepFeatures__rowIcon">
					<i :class="iconClass('stopwatch')" />
				</div>
				<div class="stepFeatures__rowText">
					<div class="stepFeatures__rowTitle">{{ $t('settings_layout_timer_enabled_label') }}</div>
					<div class="stepFeatures__rowCaption">{{ $t('settings_layout_timer_enabled_caption') }}</div>
				</div>
				<ToggleSwitch
					:model-value="store.timerEnabled"
					@update:model-value="onTimerChange"
				/>
			</div>

			<div class="stepFeatures__row">
				<div class="stepFeatures__rowIcon">
					<i :class="iconClass('magnifying-glass')" />
				</div>
				<div class="stepFeatures__rowText">
					<div class="stepFeatures__rowTitle">{{ $t('settings_layout_spotlight_search_label') }}</div>
					<div class="stepFeatures__rowCaption">{{ $t('settings_layout_spotlight_search_caption') }}</div>
				</div>
				<ToggleSwitch
					:model-value="store.spotlightSearchEnabled"
					@update:model-value="onSpotlightChange"
				/>
			</div>
		</div>

		<section class="stepFeatures__section">
			<div class="stepFeatures__sectionLabel">{{ $t('settings_layout_user_online_position_label') }}</div>
			<p class="stepFeatures__sectionCaption">{{ $t('settings_layout_description') }}</p>
			<div
				class="stepFeatures__tileRow"
				role="radiogroup"
			>
				<button
					:aria-checked="userOnlinePosition === 'left'"
					:class="['stepFeatures__tile', { 'stepFeatures__tile--active': userOnlinePosition === 'left' }]"
					@click="onUserOnlineChange('left')"
					role="radio"
					type="button"
				>
					<i
						:class="iconClass('sidebar')"
						class="stepFeatures__tileIcon"
					/>
					<span>{{ $t('settings_layout_user_online_position_left') }}</span>
				</button>
				<button
					:aria-checked="userOnlinePosition === 'right'"
					:class="['stepFeatures__tile', { 'stepFeatures__tile--active': userOnlinePosition === 'right' }]"
					@click="onUserOnlineChange('right')"
					role="radio"
					type="button"
				>
					<i
						:class="iconClass('sidebar-flip')"
						class="stepFeatures__tileIcon"
					/>
					<span>{{ $t('settings_layout_user_online_position_right') }}</span>
				</button>
				<button
					:aria-checked="userOnlinePosition === 'off'"
					:class="['stepFeatures__tile', { 'stepFeatures__tile--active': userOnlinePosition === 'off' }]"
					@click="onUserOnlineChange('off')"
					role="radio"
					type="button"
				>
					<i
						:class="iconClass('eye-slash')"
						class="stepFeatures__tileIcon"
					/>
					<span>{{ $t('settings_layout_user_online_position_off') }}</span>
				</button>
			</div>

			<div
				class="stepFeatures__row stepFeatures__row--nested"
				v-if="userOnlinePosition !== 'off'"
			>
				<div class="stepFeatures__rowIcon">
					<i :class="iconClass('user-slash')" />
				</div>
				<div class="stepFeatures__rowText">
					<div class="stepFeatures__rowTitle">{{ $t('settings_layout_user_show_offline_users') }}</div>
				</div>
				<ToggleSwitch
					:model-value="store.userOnlineShowOfflineUsers"
					@update:model-value="onShowOfflineUsersChange"
				/>
			</div>
		</section>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, inject } from 'vue';

import ToggleSwitch from 'primevue/toggleswitch';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import { CommandBus } from 'src/util/command/CommandBus';

export default defineComponent({
	components: { ToggleSwitch },
	name: 'StepFeatures',
	setup() {
		const store = useUserInterfaceSettingsStore();
		const commandBus = inject<CommandBus>('commandBus');

		const useSolid = computed<boolean>(() => store.menuItemsSolid);

		function iconClass(name: string): string {
			return useSolid.value ? `fa-solid fa-${name}` : `fa-light fa-${name}`;
		}

		const userOnlinePosition = computed<'left' | 'off' | 'right'>(() => store.userOnlinePosition);

		function onTimerChange(value: boolean): void {
			store.setTimerEnabled({ commandBus, value });
		}

		function onSpotlightChange(value: boolean): void {
			store.setSpotlightSearchEnabled(value, commandBus);
		}

		function onUserOnlineChange(position: 'left' | 'off' | 'right'): void {
			store.setUserOnlinePosition(position, commandBus);
		}

		function onShowOfflineUsersChange(value: boolean): void {
			store.setUserOnlineShowOfflineUsers(value, commandBus);
		}

		return {
			iconClass,
			onShowOfflineUsersChange,
			onSpotlightChange,
			onTimerChange,
			onUserOnlineChange,
			store,
			userOnlinePosition,
		};
	},
});
</script>

<style lang="scss" scoped>
.stepFeatures {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.stepFeatures__header {
	margin-bottom: 0;
}

.stepFeatures__title {
	font-size: var(--text-xl);
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 0 0 0.125rem;
}

.stepFeatures__caption {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	margin: 0;
}

.stepFeatures__list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.stepFeatures__row {
	align-items: center;
	background: var(--p-surface-0);
	border: 1px solid var(--p-surface-200);
	border-radius: 0.5rem;
	display: flex;
	gap: 0.875rem;
	padding: 0.625rem 1rem;
	transition: background-color 150ms ease;

	&:hover {
		background: var(--hover-light);
	}
}

.stepFeatures__rowIcon {
	color: var(--p-primary-500);
	flex-shrink: 0;
	font-size: var(--text-lg);
	width: 1.5rem;
}

.stepFeatures__rowText {
	flex: 1;
	min-width: 0;
}

.stepFeatures__rowTitle {
	color: var(--p-text-color);
	font-size: var(--text-sm-plus);
	font-weight: 500;
}

.stepFeatures__rowCaption {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
	margin-top: 0.125rem;
}

.stepFeatures__section {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.stepFeatures__sectionLabel {
	color: var(--p-text-color);
	font-size: var(--text-xs);
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
}

.stepFeatures__sectionCaption {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
	margin: 0 0 0.25rem;
}

.stepFeatures__row--nested {
	margin-top: 0.25rem;
}

.stepFeatures__tileRow {
	display: grid;
	gap: 0.5rem;
	grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stepFeatures__tile {
	align-items: center;
	background: var(--p-surface-0);
	border: 2px solid var(--p-surface-200);
	border-radius: 0.5rem;
	color: var(--p-text-color);
	cursor: pointer;
	display: flex;
	flex-direction: column;
	font-size: var(--text-xs-plus);
	gap: 0.4rem;
	padding: 0.75rem 0.5rem;
	transition:
		background-color 150ms ease,
		border-color 150ms ease;

	&:hover:not(.stepFeatures__tile--active) {
		background: var(--hover-light);
	}
}

.stepFeatures__tile--active {
	background: color-mix(in srgb, var(--p-primary-500) 8%, transparent);
	border-color: var(--p-primary-500);
}

.stepFeatures__tileIcon {
	font-size: var(--text-lg);
}
</style>

<style lang="scss">
.body--dark .stepFeatures__row {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepFeatures__row:hover {
	background: var(--hover-dark);
}

.body--dark .stepFeatures__tile {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepFeatures__tile:hover:not(.stepFeatures__tile--active) {
	background: var(--hover-dark);
}

.body--dark .stepFeatures__tile--active {
	background: color-mix(in srgb, var(--p-primary-400) 12%, transparent);
	border-color: var(--p-primary-400);
}
</style>
